import { useEffect, useRef } from "react";
import { CalendarCheck, UtensilsCrossed, Flower2, Music2, ParkingCircle, Users } from "lucide-react";
import { useLang } from "../LanguageContext";

const serviceIcons = [CalendarCheck, UtensilsCrossed, Flower2, Music2, ParkingCircle, Users];
const SERVICIOS_BG = "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/wz869ms1_Galeria%20exterior.jpg";

export default function ServiciosSection() {
  const { t } = useLang();
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("fade-in-visible")),
      { threshold: 0.1 }
    );
    const els = ref.current?.querySelectorAll(".fade-in-item");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="servicios"
      ref={ref}
      data-testid="servicios-section"
      className="relative bg-[#F5F0E8] py-24 md:py-36 overflow-hidden"
    >
      {/* Translucid background with slow drift */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 bg-parallax"
          style={{
            backgroundImage: `url(${SERVICIOS_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.1,
          }}
        />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-end mb-16">
          <div>
            <p
              className="fade-in-item text-xs uppercase tracking-[0.4em] text-[#C8A96E] mb-4"
              style={{ fontFamily: "Lato, sans-serif" }}
            >
              {t.servicios.caption}
            </p>
            <div className="fade-in-item w-12 h-px bg-[#C8A96E] mb-8" />
            <h2
              className="fade-in-item text-4xl md:text-5xl font-light text-[#1A1A18] leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {t.servicios.heading}
            </h2>
          </div>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#C8A96E]/20">
          {t.servicios.items.map((item, i) => {
            const Icon = serviceIcons[i];
            const isParking = i === 4;
            const handleClick = isParking
              ? () => {
                  const target = document.getElementById("parking-info");
                  if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
                }
              : undefined;
            return (
              <div
                key={i}
                data-testid={`servicio-item-${i}`}
                onClick={handleClick}
                role={isParking ? "button" : undefined}
                tabIndex={isParking ? 0 : undefined}
                onKeyDown={
                  isParking
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleClick();
                        }
                      }
                    : undefined
                }
                className={`fade-in-item bg-[#F5F0E8] p-8 md:p-10 group hover:bg-[#E8DCC8] transition-colors duration-300 ${
                  isParking ? "cursor-pointer" : ""
                }`}
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center border border-[#C8A96E]/30 mb-6 group-hover:border-[#C8A96E] group-hover:bg-[#C8A96E]/10 transition-all duration-300">
                  <Icon size={20} className="text-[#C8A96E]" strokeWidth={1.5} />
                </div>
                <h3
                  className="text-lg font-medium text-[#1A1A18] mb-3"
                  style={{ fontFamily: "'Libre Baskerville', serif" }}
                >
                  {item.title}
                </h3>
                <p
                  className="text-sm text-[#1A1A18]/60 leading-relaxed"
                  style={{ fontFamily: "Lato, sans-serif" }}
                >
                  {item.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
